import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Palette, LayoutDashboard, RefreshCw, Globe2, SlidersHorizontal, Puzzle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { resolveApiBase } from '../lib/http';

const API_URL = resolveApiBase().replace(/\/api\/?$/, '');

async function readApiError(response) {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) return data.detail.map((d) => d.msg || String(d)).join(', ');
    return JSON.stringify(data.detail || data);
  } catch {
    try {
      const text = await response.text();
      return text || `HTTP ${response.status}`;
    } catch {
      return `HTTP ${response.status}`;
    }
  }
}

const COMMERCE_IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-popups allow-modals';
const OPERATOR_CONTENT_BG = 'bg-[#f8fafc]';

const RESOURCE_LABELS = {
  widget_configs: 'Widget configuration',
  inquiries: 'Forms / inquiries',
  bookings: 'Bookings / reservations',
  reviews: 'Reviews',
  menu_items: 'Menu items',
  products: 'Products',
  services: 'Services',
  operators: 'Team / staff',
  tables: 'Tables',
  leads: 'Leads / quizzes',
  subscribers: 'Subscribers',
  comments: 'Comments',
};

const SECTION_ALIASES = {
  quiz: 'widgets',
  survey: 'widgets',
  poll: 'widgets',
  widget_configs: 'widgets',
  widget_config: 'widgets',
  widgets_config: 'widgets',
  form: 'inquiries',
  forms: 'inquiries',
  contact: 'inquiries',
  booking: 'bookings',
  reservation: 'bookings',
  review: 'reviews',
  testimonial: 'reviews',
  subscriber: 'subscribers',
  newsletter: 'subscribers',
  menu_items: 'menu',
};

const normalizeSection = (section) => SECTION_ALIASES[section] || section;

const OperatorCockpit = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const operateSection = normalizeSection(searchParams.get('section') || '');
  const viewParam = searchParams.get('view') || '';
  const highlightInstance = searchParams.get('instance') || '';
  const rawCommerceTab = searchParams.get('commerceTab') || 'currency';
  const commerceTabParam = rawCommerceTab === 'language' ? 'currency' : rawCommerceTab;

  const isCommerceView = viewParam === 'commerce' || operateSection === 'commerce';
  const isWidgetView = viewParam === 'widgets' || operateSection === 'widgets';
  const [mainView, setMainView] = useState(isCommerceView ? 'commerce' : (isWidgetView ? 'widgets' : 'dashboard'));
  const [commerceTab, setCommerceTab] = useState(commerceTabParam);

  const [website, setWebsite] = useState(null);
  const [genreLabel, setGenreLabel] = useState('');
  const [dashboardHtml, setDashboardHtml] = useState('');
  const [widgetSettingsOpen, setWidgetSettingsOpen] = useState(false);
  const [widgetSections, setWidgetSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';

  const loadCockpit = useCallback(async ({ reprovision = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      if (!token) {
        throw new Error('You are not signed in. Please log in and try again.');
      }
      const headers = { Authorization: `Bearer ${token}` };

      if (reprovision) {
        const provRes = await fetch(`${API_URL}/api/websites/${websiteId}/operator-dashboard/provision`, {
          method: 'POST',
          headers,
        });
        if (!provRes.ok) {
          throw new Error(await readApiError(provRes));
        }
      }

      const siteRes = await fetch(`${API_URL}/api/websites/${websiteId}`, { headers });
      if (!siteRes.ok) {
        throw new Error(await readApiError(siteRes));
      }

      const site = await siteRes.json();
      setWebsite(site);

      if (!isCommerceView) {
        let dashUrl = `${API_URL}/api/websites/${websiteId}/operator-dashboard?_t=${Date.now()}`;
        const sectionForLoad = isWidgetView ? 'widgets' : (operateSection || '');
        if (sectionForLoad && sectionForLoad !== 'commerce') {
          dashUrl += `&section=${encodeURIComponent(sectionForLoad)}`;
        }
        if (highlightInstance) {
          dashUrl += `&instance=${encodeURIComponent(highlightInstance)}`;
        }
        let dashRes = await fetch(dashUrl, { headers, cache: 'no-store' });
        if (!dashRes.ok && dashRes.status === 404 && !reprovision) {
          const provRes = await fetch(`${API_URL}/api/websites/${websiteId}/operator-dashboard/provision`, {
            method: 'POST',
            headers,
          });
          if (provRes.ok) {
            dashRes = await fetch(dashUrl, { headers, cache: 'no-store' });
          }
        }
        if (!dashRes.ok) {
          throw new Error(await readApiError(dashRes));
        }
        setDashboardHtml(await dashRes.text());
      } else {
        setDashboardHtml('');
      }

      const genreId = site.genre_id || site.category;
      if (genreId) {
        try {
          const genreRes = await fetch(`${API_URL}/api/genre-dashboards/${genreId}`);
          if (genreRes.ok) {
            const genreCfg = await genreRes.json();
            setGenreLabel(genreCfg.label || genreId);
          } else {
            setGenreLabel(genreId);
          }
        } catch {
          setGenreLabel(genreId);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load operator cockpit');
    } finally {
      setLoading(false);
    }
  }, [websiteId, operateSection, token, isCommerceView, isWidgetView, highlightInstance]);

  useEffect(() => {
    loadCockpit();
  }, [loadCockpit]);

  useEffect(() => {
    try {
      setWidgetSections(JSON.parse(localStorage.getItem(`operator_widget_sections_${websiteId}`) || '[]'));
    } catch {
      setWidgetSections([]);
    }
  }, [websiteId, widgetSettingsOpen]);

  useEffect(() => {
    setMainView(isCommerceView ? 'commerce' : (isWidgetView ? 'widgets' : 'dashboard'));
  }, [isCommerceView, isWidgetView]);

  useEffect(() => {
    if (commerceTabParam) setCommerceTab(commerceTabParam);
  }, [commerceTabParam]);

  useEffect(() => {
    const operateChannel = typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('snagcart-operate-sync')
      : null;
    const relayOperateChange = (payload) => {
      operateChannel?.postMessage(payload);
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(payload, '*');
        }
      } catch {
        /* ignore cross-window errors */
      }
    };
    const onMessage = (event) => {
      if (event.data?.type === 'operator-data-changed') {
        relayOperateChange(event.data);
      }
      if (event.data?.type === 'operator-close') {
        navigate('/dashboard');
      }
      if (event.data?.type === 'operator-open-commerce') {
        setMainView('commerce');
        if (event.data.tab) setCommerceTab(event.data.tab);
        setSearchParams({ view: 'commerce', commerceTab: event.data.tab || 'currency' });
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      operateChannel?.close();
    };
  }, [navigate, setSearchParams]);

  const switchView = (view, tabOrSection = commerceTab) => {
    setMainView(view);
    if (view === 'commerce') {
      setSearchParams({ view: 'commerce', commerceTab: tabOrSection });
    } else if (view === 'widgets') {
      setSearchParams({ view: 'widgets', section: 'widgets' });
    } else if (view === 'dashboard' && tabOrSection && typeof tabOrSection === 'string' && tabOrSection !== commerceTab) {
      setSearchParams({ section: normalizeSection(tabOrSection) });
    } else {
      setSearchParams({});
    }
  };

  const genreStr = String(website?.genre_id || website?.category || '').toLowerCase();
  const hasCommerce = true;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <LayoutDashboard className="w-10 h-10 text-rose-400" />
        <p className="text-zinc-300">{error}</p>
        <div className="flex gap-2 flex-wrap justify-center">
          <Button onClick={() => loadCockpit({ reprovision: true })} variant="outline" className="border-zinc-700 text-zinc-200">
            <RefreshCw className="w-4 h-4 mr-2" />
            Provision &amp; retry
          </Button>
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-zinc-700 text-zinc-200">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-zinc-950 overflow-hidden" data-testid="operator-cockpit">
      {!isWidgetView && (
      <header className="flex items-center justify-between gap-3 px-4 py-2 border-b border-white/10 bg-zinc-900/90 backdrop-blur flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Sites
          </Button>
          <div className="h-5 w-px bg-zinc-700" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{website?.name || 'Operator cockpit'}</p>
            <p className="text-[11px] text-zinc-500 truncate">
              {genreLabel ? `${genreLabel} · operator` : 'Operator'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button onClick={loadCockpit} size="sm" variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800" title="Refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button onClick={() => navigate(`/edit/${websiteId}`)} size="sm" variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800">
            <Palette className="w-3.5 h-3.5 mr-1.5" />
            Edit site
          </Button>
          <Button onClick={() => setWidgetSettingsOpen(true)} size="sm" variant="outline" className="border-violet-700/60 text-violet-200 hover:bg-violet-900/30">
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
            Widget settings
          </Button>
        </div>
      </header>
      )}

      {isWidgetView && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-white/10 bg-zinc-900/95 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Button onClick={() => navigate(`/edit/${websiteId}`)} variant="ghost" size="sm" className="text-zinc-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to editor
            </Button>
            <div className="h-5 w-px bg-zinc-700" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">Widget Dashboard</p>
              <p className="text-[11px] text-zinc-500 truncate">{website?.name || 'Configure widget instances'}</p>
            </div>
          </div>
          <Button onClick={loadCockpit} size="sm" variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800" title="Refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {!isWidgetView && widgetSettingsOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" data-testid="operator-widget-settings-modal">
          <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-zinc-950 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <h2 className="text-lg font-bold text-white">Widget settings</h2>
                <p className="text-sm text-zinc-400">Edit the operator entries that feed widgets on your template pages.</p>
              </div>
              <button type="button" onClick={() => setWidgetSettingsOpen(false)} className="text-zinc-500 hover:text-white text-xl">×</button>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-3">
              {widgetSections.length > 0 && (
                <div className="sm:col-span-2 rounded-xl border border-violet-500/30 bg-violet-500/10 p-3">
                  <p className="text-xs uppercase tracking-wide text-violet-300 mb-2">Widgets added from editor</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {widgetSections.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        onClick={() => { setWidgetSettingsOpen(false); switchView('widgets'); }}
                        className="rounded-lg border border-violet-500/30 bg-zinc-950/60 p-3 text-left hover:bg-violet-950/40 transition"
                      >
                        <p className="text-sm font-semibold text-white">{entry.name}</p>
                        <p className="text-xs text-violet-200 mt-1">Edit {RESOURCE_LABELS[entry.resource] || entry.resource}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {[
                ['Widget Dashboard', 'widgets'], ['Menu items', 'menu'], ['Bookings', 'bookings'], ['Reviews', 'reviews'], ['Comments', 'comments'], ['Inquiries / forms', 'inquiries'],
                ['Services', 'services'], ['Products', 'products'], ['Team / staff', 'operators'], ['Tables', 'tables'],
                ['Leads / quizzes', 'leads'], ['Subscribers', 'subscribers'],
              ].map(([label, section]) => (
                <button
                  key={section}
                  type="button"
                  onClick={() => { setWidgetSettingsOpen(false); switchView('dashboard', section); }}
                  className="rounded-xl border border-white/10 bg-zinc-900 p-4 text-left hover:bg-zinc-800 hover:border-violet-500/50 transition"
                >
                  <p className="text-sm font-semibold text-white">{label}</p>
                  <p className="text-xs text-zinc-500 mt-1">Updates sync back to matching template widgets.</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {!isWidgetView && (
      <div className="flex items-center gap-1 px-4 py-2 border-b border-white/10 bg-zinc-900/50 flex-shrink-0">
        <button
          type="button"
          data-testid="operator-tab-dashboard"
          onClick={() => switchView('dashboard')}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
            mainView === 'dashboard' ? 'bg-rose-500/20 text-rose-300' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
          }`}
        >
          <LayoutDashboard className="w-4 h-4" />
          Dashboard
        </button>
        {hasCommerce && (
          <button
            type="button"
            data-testid="operator-tab-commerce"
            onClick={() => switchView('commerce', 'currency')}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
              mainView === 'commerce' ? 'bg-violet-500/20 text-violet-300' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Globe2 className="w-4 h-4" />
            Commerce+
          </button>
        )}
        <button
          type="button"
          data-testid="operator-tab-widgets"
          onClick={() => switchView('widgets')}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
            mainView === 'widgets' ? 'bg-emerald-500/20 text-emerald-300' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
          }`}
        >
          <Puzzle className="w-4 h-4" />
          Widget Dashboard
        </button>
      </div>
      )}

      {mainView === 'commerce' && hasCommerce && (
        <iframe
          title="Commerce+"
          src={`/sites/${websiteId}/operate-commerce?tab=${encodeURIComponent(commerceTab)}`}
          className={`w-full flex-1 border-0 min-h-0 ${OPERATOR_CONTENT_BG}`}
          sandbox={COMMERCE_IFRAME_SANDBOX}
        />
      )}

      {(mainView === 'widgets' || mainView === 'dashboard') && (
        <iframe
          srcDoc={dashboardHtml}
          className={`flex-1 w-full border-0 min-h-0 ${OPERATOR_CONTENT_BG}`}
          title={mainView === 'widgets' ? 'Widget Dashboard' : `Operator dashboard — ${website?.name || websiteId}`}
          sandbox={COMMERCE_IFRAME_SANDBOX}
        />
      )}
    </div>
  );
};

export default OperatorCockpit;
